/**
 * Formulário de configuração global de billing.
 *
 * Campos: credit_usd_cost, usd_brl_rate, trial_days, trial_credits.
 * Salva via PUT /api/v1/admin/billing/config.
 */
import { useState, useEffect } from 'react';
import { Button } from '../../design-system/components/Button';
import { Card } from '../../design-system/components/Card';
import { TextField } from '../../design-system/components/TextField';
import { ErrorState } from '../../shared/components/ErrorState';
import { Spinner } from '../../design-system/components/Spinner';
import { useBillingConfig, useUpdateBillingConfig } from './hooks';

export function ConfigForm() {
  const query = useBillingConfig();
  const mutation = useUpdateBillingConfig();

  const [creditUsdCost, setCreditUsdCost] = useState('');
  const [usdBrlRate, setUsdBrlRate] = useState('');
  const [trialDays, setTrialDays] = useState('');
  const [trialCredits, setTrialCredits] = useState('');
  const [saved, setSaved] = useState(false);
  const [validacao, setValidacao] = useState<string | null>(null);

  // Preenche os campos quando os dados chegam.
  useEffect(() => {
    if (query.data !== undefined) {
      setCreditUsdCost(String(query.data.credit_usd_cost));
      setUsdBrlRate(String(query.data.usd_brl_rate));
      setTrialDays(String(query.data.trial_days));
      setTrialCredits(String(query.data.trial_credits));
    }
  }, [query.data]);

  if (query.isPending) {
    return (
      <div className="center">
        <Spinner label="Carregando configuração" />
      </div>
    );
  }

  if (query.isError) {
    return (
      <ErrorState
        error={query.error}
        onRetry={() => query.refetch()}
        title="Não foi possível carregar a configuração de billing"
      />
    );
  }

  function handleSalvar() {
    setSaved(false);
    setValidacao(null);
    const creditUsdCostNum = parseFloat(creditUsdCost);
    const usdBrlRateNum = parseFloat(usdBrlRate);
    const trialDaysNum = parseInt(trialDays, 10);
    const trialCreditsNum = parseInt(trialCredits, 10);
    if (!(creditUsdCostNum > 0) || !(usdBrlRateNum > 0)) {
      setValidacao('Custo por crédito e taxa USD → BRL devem ser números maiores que zero.');
      return;
    }
    if (!(trialDaysNum >= 0) || !(trialCreditsNum >= 0)) {
      setValidacao('Dias e créditos de trial devem ser números maiores ou iguais a zero.');
      return;
    }
    mutation.mutate(
      {
        credit_usd_cost: creditUsdCostNum,
        usd_brl_rate: usdBrlRateNum,
        trial_days: trialDaysNum,
        trial_credits: trialCreditsNum,
      },
      {
        onSuccess: () => setSaved(true),
      },
    );
  }

  return (
    <Card>
      <h2 className="dash-section__title">Configuração global</h2>
      <p className="muted">Parâmetros de precificação e trial aplicados a todos os tenants.</p>
      <div className="dash-grid">
        <TextField
          label="Custo por crédito (USD)"
          type="number"
          step="0.0001"
          min="0"
          value={creditUsdCost}
          onChange={(e) => {
            setCreditUsdCost(e.target.value);
            setSaved(false);
          }}
          helperText="Ex: 0.0010"
        />
        <TextField
          label="Taxa USD → BRL"
          type="number"
          step="0.01"
          min="0"
          value={usdBrlRate}
          onChange={(e) => {
            setUsdBrlRate(e.target.value);
            setSaved(false);
          }}
          helperText="Ex: 5.20"
        />
        <TextField
          label="Dias de trial"
          type="number"
          step="1"
          min="0"
          value={trialDays}
          onChange={(e) => {
            setTrialDays(e.target.value);
            setSaved(false);
          }}
          helperText="Ex: 14"
        />
        <TextField
          label="Créditos de trial"
          type="number"
          step="1"
          min="0"
          value={trialCredits}
          onChange={(e) => {
            setTrialCredits(e.target.value);
            setSaved(false);
          }}
          helperText="Ex: 500"
        />
      </div>
      <div className="row row--sm-gap billing-admin__row-mt-lg">
        <Button onClick={handleSalvar} isLoading={mutation.isPending} disabled={mutation.isPending}>
          Salvar configuração
        </Button>
        {validacao !== null && (
          <span className="label" role="alert">
            {validacao}
          </span>
        )}
        {mutation.isError && (
          <ErrorState error={mutation.error} title="Erro ao salvar configuração" />
        )}
        {saved && (
          <span className="label" role="status" aria-live="polite">
            Configuração salva com sucesso.
          </span>
        )}
      </div>
    </Card>
  );
}
